import React from 'react';
import styled, { FlattenSimpleInterpolation, css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { StyledErrorMessage } from '../Input/Input';

type Props = {
  readonly name?: string;
  readonly regex?: RegExp;
  readonly value: string | readonly string[];
  readonly onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly hasError?: boolean | React.ReactNode;
  readonly styles?: FlattenSimpleInterpolation;
  readonly required?: boolean;
  readonly errorPosition?: 'top' | 'bottom';
};

const StyledTextarea = styled.textarea<{
  readonly hasError: boolean;
  readonly styles: FlattenSimpleInterpolation;
}>`
  width: 100%;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.color('#B2B1BA')};
  outline: none;
  border-radius: 4px;
  padding: 8px;
  resize: none;

  ${({ hasError }) =>
    hasError &&
    css`
      border: solid 1px #dd6c6c;
    `}

  ${({ styles }) => styles}

  &:focus-within {
    border: 1px solid #239ae7;
  }
`;

const ALLOWED_KEYS = [
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Enter',
  'Backspace',
  'Delete',
  'Home',
  'End',
];

export const Textarea = ({
  name,
  regex,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  hasError,
  styles = {} as FlattenSimpleInterpolation,
  required,
  errorPosition = 'bottom',
}: Props) => {
  const defaultRegex = /[\s\S]*/;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (
      (regex || defaultRegex).test(event.target.value) ||
      event.target.value === ''
    ) {
      onChange(event);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!(regex || defaultRegex).test(event.clipboardData.getData('text'))) {
      event.preventDefault();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (ALLOWED_KEYS.includes(event.key)) {
      return;
    }

    if (event.getModifierState('Control') || event.getModifierState('Meta')) {
      return;
    }

    if (!(regex || defaultRegex).test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <StyledTextarea
        name={name}
        hasError={!!hasError || (!!required && !value)}
        styles={styles}
        disabled={Boolean(disabled)}
        onBlur={onBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        value={value}
      />
      {hasError && typeof hasError !== 'boolean' && (
        <StyledErrorMessage errorPosition={errorPosition}>
          {hasError}
        </StyledErrorMessage>
      )}
      {required && !value && (
        <StyledErrorMessage errorPosition={errorPosition}>
          <FormattedMessage defaultMessage="Required Field" id="H3r5Hy" />
        </StyledErrorMessage>
      )}
    </div>
  );
};
